import { useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import useCustomTableFilters from "../../../hooks/useCustomTableFilters";
import { RT6FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import TrComponent from "../../../components/custom/merchandise/TrComponent";
import useColumnSelector from "../../../hooks/useColumnSelector";
import { filterRanges, generateFilterMarkup, generateRanges } from "../../../utils/rangeDropdownFilter";
import ReactTable from "react-table-6";
import ReactTablePagination from "../../../components/custom/growth/ProductsCVTablePagination";
import { trellisPalette } from "../../../components/custom/analytics/palettes";
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  parseFormattedCurrency,
  parseFormattedNumber,
  parseFormattedPercentage,
} from "../../../utils/formatNumber";
import { useAppSelector } from "../../../redux/hooks";
import {
  extendAllEntityMetricOptions,
  useStandardEntityMetrics,
} from "../../merchandising/components/tables/metricUtils";
import getMetricSpec from "../../merchandising/utils/metrics";
import useTableSettings from "../../../hooks/useTableSettings";
import { sortTableColumns } from "../../../utils/tableUtils";
import { CategoryCell } from "../../../components/custom/merchandise/cells/CategoryCell";
import { TrendCell } from "../../merchandising/components/cells/TrendCell";

const getColumnSpecs = (marketPlace, t) => {
  return [
    {
      header: t(($) => $.pricingv2["Category"]),
      type: "category",
      key: "category_name",
      options: {
        className: "text-left font-weight-bold",
        width: 350,
        showDot: false,
        fixedLeft: true,
        hideFilter: true,
        isStatic: true,
      },
    },
    {
      header: t(($) => $.pricingv2["Pricing Plans"]),
      type: "number",
      key: "pricing_plan_cnt",
      decimalPlaces: 0,
      options: {
        showDot: false,
        isStatic: true,
        checked: true,
        toolTip: t(($) => $.pricingv2["The number of pricing plans in a category"]),
      },
    },

    {
      header: t(($) => $.pricingv2["Ad Spend"]),
      type: "currency",
      key: "cost",
      options: {
        hideCompare: false,
        checked: true,
      },
    },
    {
      header: t(($) => $.pricingv2["Units"]),
      type: "number",
      key: "total_units",
      options: {
        hideCompare: false,
        checked: true,
      },
    },
    {
      header: t(($) => $.pricingv2["Sales"]),
      type: "currency",
      key: "total_sales",
      options: {
        hideCompare: false,
        checked: true,
      },
    },
    {
      header: t(($) => $.pricingv2["Margin"]),
      type: "percent",
      key: "margin",
      options: {
        noColor: true,
        delta: true,
        checked: true,
      },
    },
    {
      header: t(($) => $.pricingv2["PPV"]),
      type: "currency",
      key: "profit_per_page_view",
      options: {
        checked: true,
        formatter: (p) =>
          formatCurrency(p.value, {
            marketPlace: marketPlace.marketPlace,
          }),
        accessor: (p) => p.profit_per_page_view,
      },
    },
    {
      header: t(($) => $.pricingv2["Trends"]),
      type: "trends",
      key: "trends_score",
      options: {
        className: "text-center",
        checked: true,
      },
    },
  ];
};

const CategoryTable = ({ stats }) => {
  const { t } = useTranslation(["pricingv2", "metrics"]);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const columnSpecs = useMemo(() => getColumnSpecs(marketPlace.marketPlace, t), [marketPlace, t]);

  const [searchFilter, setSearchFilter] = useState("");
  const [clearFilters, setClearFilters] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const TABLE_ID = "categoryPricingTable";
  const tableRef = useRef();
  const { customFilters, setCustomFilters, applyCustomFilters } = useCustomTableFilters(TABLE_ID);

  const { savedTableSettings: savedTablePageSize, updateTableSettings: setSavedTablePageSize } = useTableSettings(
    TABLE_ID,
    "tablePageSize",
    5,
  );

  const { savedTableSettings: savedTableSortMethod, updateTableSettings: setSavedTableSortMethod } = useTableSettings(
    TABLE_ID,
    "tableSorting",
    [],
  );

  const { savedTableSettings: savedColumnOrder, updateTableSettings: setSavedColumnOrder } = useTableSettings(
    TABLE_ID,
    "tableColOrder",
    [],
  );

  const makeColumn = (data, marketPlace) => (header, key, type, options) => {
    const { hideFilter } = options ?? {};
    const metricSpec = getMetricSpec(key);

    const defaultAccessor = (p) => p[key];
    const accessor = options?.accessor ?? defaultAccessor;
    const defaultFormat = ({ value }) => value;

    const fmt =
      options?.formatter ??
      {
        currency: ({ value, overrideValue }) =>
          formatCurrency(overrideValue ? overrideValue : value, {
            marketPlace: marketPlace.marketPlace,
            decimalPlaces: options?.decimalPlaces ?? 0,
            addPlus: options?.addPlus ?? false,
          }),
        number: ({ value }) => formatNumber(value, { decimalPlaces: 1 }),
        percent: ({ value }) =>
          formatPercent(value, {
            decimalPlaces: 1,
          }),
        category: ({ original }) => (
          <CategoryCell
            original={original}
            buildLink={(categoryId) => `/user/pricing/management/category/${categoryId}`}
          />
        ),
        trends: (props) => {
          return <TrendCell value={props.original.trends_score} trends={props.original.trends} />;
        },
        percentDiff: ({ value }) => (
          <span
            style={{
              color: value > 0 ? trellisPalette[12] : trellisPalette[17],
            }}
          >
            {formatPercent(value, {
              decimalPlaces: 1,
              addPlus: true,
            })}
          </span>
        ),
      }?.[type] ??
      defaultFormat;

    const parser =
      {
        currency: parseFormattedCurrency,
        number: parseFormattedNumber,
        percent: parseFormattedPercentage,
      }?.[type] ?? defaultFormat;

    // Handle tooltips to support metric selection menu descriptions
    let { toolTip } = options;

    if (!toolTip) {
      toolTip = t(($) => $.metrics.byId[key].toolTip, { defaultValue: "" });
    }

    // Handle percent filter formatting
    if (type === "percent") {
      data = data.map((row) => {
        Object.keys(row).forEach((propertyKey) => {
          if (propertyKey === key) {
            row["formatted_" + propertyKey] = row[propertyKey] * 100;
          }
        });

        return row;
      });
    }

    const c = (props) => {
      if (options?.showDot ?? true) {
        const percentKey = `${key}_percent`;
        const percent = props.original?.[percentKey] ?? null;
        const deltaKey = `${key}_delta`;
        const deltaValue = props.original?.[deltaKey];
        const isNegative = options?.isNegative ?? false;
        const noColor = options?.noColor ?? false;
        const params = options?.params ?? {};

        let color = "#666";
        if (!noColor && percent > 0) {
          color = isNegative ? trellisPalette[17] : trellisPalette[12];
        }
        if (!noColor && percent < 0) {
          color = isNegative ? trellisPalette[12] : trellisPalette[17];
        }

        return (
          <div>
            {fmt({ ...props, key, params })}
            <br />
            {options?.showBlank ?
              <span>-</span>
            : <span
                style={{
                  color,
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                }}
              >
                {!options?.hideCompare &&
                  (options?.delta ? (deltaValue > 0 ? "+" : "") + fmt({ value: deltaValue })
                  : percent === null ? ""
                  : formatPercent(percent, {
                      decimalPlaces: 1,
                      addPlus: true,
                    }))}
              </span>
            }
          </div>
        );
      }
      return fmt({ ...props });
    };

    return {
      Header: header,
      id: key,
      accessor,
      checked: options?.checked ?? true,
      maxWidth: options?.maxWidth,
      minWidth: options?.minWidth,
      isStatic: options?.isStatic,
      width: options?.width,
      height: 30,
      grouping: metricSpec.grouping,
      format: type,
      toolTip,
      className: options?.className ?? "text-end",
      headerClassName: options?.className ?? "text-end",
      Cell: c,
      style: { height: "60px" },
      filterMethod: (filter, row) => {
        const formattedPercentAccessor = (p) => p[`formatted_${key}`];
        const value = type === "percent" ? formattedPercentAccessor(row._original) : accessor(row._original);
        return filterRanges(filter, value, parser, marketPlace.marketPlace);
      },
      Filter: ({ filter, onChange }) => {
        if (hideFilter) {
          return null;
        }

        return generateFilterMarkup(filter, onChange, key, "range", type, {
          ranges: generateRanges(
            data ?? [],
            5,
            type === "percent" ? `formatted_${key}` : key,
            "number",
            marketPlace.marketPlace,
          ),
          marketPlace: marketPlace.marketPlace,
        });
      },
    };
  };

  // TODO: filter data at higher level, pass as prop to table
  const data = useMemo(
    () => {
      return applyCustomFilters(
        stats?.filter((p) => {
          if (searchFilter) {
            const searchable = `${p.category_name} ${p.name}`;
            return searchable.toLowerCase()?.indexOf(searchFilter.toLowerCase()) > -1;
          }
          return true;
        }),
      );
    },
    // Below linting disabled to take clearFilters in to recalcalculate data on filter reset
    // Without it, filter specs from react-table must be stored at this level and manually cleared on each reset

    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [stats, searchFilter, clearFilters, customFilters],
  );

  const createColumn = makeColumn(data, marketPlace);
  const standardMetricIds = useStandardEntityMetrics("category");
  const [columns, columnSelectorProps] = useColumnSelector(
    () => {
      let specifiedColumns = columnSpecs.map((c) => {
        return createColumn(c.header, c.key, c.type, c.options);
      });
      specifiedColumns = extendAllEntityMetricOptions(
        standardMetricIds,
        stats,
        specifiedColumns,
        marketPlace.marketPlace,
      );

      return sortTableColumns(specifiedColumns, savedColumnOrder);
    },
    [columnSpecs, stats, standardMetricIds],
    TABLE_ID,
  );
  const columnOrder = columns.map((col) => col.id);

  return (
    <ReactTable
      ref={tableRef}
      showPaginationBottom={false}
      showPaginationTop
      PaginationComponent={ReactTablePagination}
      columns={columns}
      searchWidth={"250px"}
      data={data}
      defaultPageSize={savedTablePageSize || 10}
      minRows={5}
      className={`-highlight ${TABLE_ID}`}
      defaultSorted={savedTableSortMethod || [{ id: "sales", desc: false }]}
      filterable={showFilters}
      state={columnOrder}
      onSortedChange={(newSorted) => {
        setSavedTableSortMethod(newSorted);
      }}
      onPageSizeChange={(pageSize) => {
        setSavedTablePageSize(pageSize);
      }}
      TrComponent={TrComponent}
      showSelectRows={true}
      getTheadFilterProps={(props) => {
        if (clearFilters) {
          props.filtered.forEach((filter) => {
            filter.value = "all";
          });
          setClearFilters(false);
        }
        return { style: { display: "flex" } };
      }}
      getPaginationProps={() => ({
        updateSearch: setSearchFilter,
        clearFilters: () => {
          setSearchFilter("");
          setClearFilters(true);
        },
        hideFilters: false,
        showFilters: showFilters,
        setShowFilters: setShowFilters,
        clearSearchTermFilters: () => setSearchFilter(""),
        searchTerm: searchFilter,
        table: "plan-categories",
        style: { marginBottom: "2rem" },
        carouselLayout: false,
        disableSearchFocus: true,
        filterControlsWidth: "70%",
        columnSelectorProps: columnSelectorProps,
        filterWidget: true,
        hideFilterToggleIcon: true,
        savedColumnOrder: savedColumnOrder,
        setSavedColumnOrder: setSavedColumnOrder,
        entityType: "category",
        children: (
          <div className="d-flex justify-content-start w-100 h-100 align-items-center px-2">
            <RT6FilterWidget
              customFilters={customFilters}
              setCustomFilters={setCustomFilters}
              tableRef={tableRef}
              excludedFilterColumns={["category_name", "trends_score"]}
            />
          </div>
        ),
      })}
    />
  );
};

export default CategoryTable;
